import {Delivery, DeliveryState, PickupEvent, Recipient, Tenant, TenantUser, getOccupancyDeliveries} from "./types";
import {Barcode, NfcCard} from "@variocube/app-api";
import * as React from "react";
import {Fragment, PropsWithChildren, useCallback, useState} from "react";
import {
    Dialog,
    DialogContent,
    useBarcodeListener,
    useCardListener,
    usePlatform,
    useStorageItemValue
} from "@variocube/cube-ui";
import {findByPickupKey, findByStorageKey, useKeyInput} from "./keys";
import {logger} from "./logger";
import {useLocalization} from "../i18n";

export interface KeySupportProps {
    onStorage?: (delivery: Delivery) => void;
    onPickup?: (event: PickupEvent) => void;
    onCreateDelivery?: (delivery: Delivery) => void;
    onAdminLogin?: (user: TenantUser) => void;
}

enum State {
    Closed = 0,
    Loading = 1,
    NotFound = 2,
    CannotStore = 3,
}

const log = logger.sub("KeySupport");

export function KeySupport({onPickup, onStorage, onCreateDelivery, onAdminLogin, children}: PropsWithChildren<KeySupportProps>) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const deliveries = useStorageItemValue<Delivery[]>("deliveries");
    const tenant = useStorageItemValue<Tenant>("tenant");
    const [state, setState] = useState(State.Closed);

    const handleClose = useCallback(() => setState(State.Closed), []);

    const handleKey = useCallback(async (key: string) => {
        log.info("Receiving key", key);

        const trimmed = atob(key).trim();
        if (trimmed.length === 0) {
            log.info("Received empty key. Skipping key.");
            return;
        }

        // key can be a json or a code or an url
        // depends on the context, we treat it accordingly to use case
        try {
            // since the current QR code scanner can send multiple chunks when reading a single QR code,
            // ensure that the json data can only be parsed when it is properly formatted
            if (trimmed.startsWith('{') || trimmed.endsWith('}')) {
                if (!trimmed.endsWith('}') || !trimmed.startsWith('{')) {
                    log.error("Invalid delivery json provided. Delivery not create.");
                    return;
                }
                const delivery = JSON.parse(trimmed) as Delivery;
                delivery.delivery = {
                    ...(delivery.delivery ?? {}),
                    state: DeliveryState.Stored
                };
                if (onCreateDelivery) {
                    setState(State.Loading);
                    log.info("Creating delivery from key", delivery);
                    onCreateDelivery(delivery);
                    return;
                }
            }

            setState(State.Loading);

            if (onStorage) {
                log.info("Looking up storage key", trimmed);
                let delivery = findByStorageKey(deliveries ?? [], trimmed);
                if (!delivery) {
                    const urlIndex = trimmed.indexOf("https://");
                    if (urlIndex) {
                        delivery = findByStorageKey(deliveries ?? [], trimmed.substring(urlIndex));
                    }
                }
                if (delivery) {
                    log.info("Found delivery", delivery);
                    if (delivery.delivery.state == DeliveryState.Created || delivery.delivery.state == DeliveryState.Reserved) {
                        onStorage(delivery);
                    } else {
                        setState(State.CannotStore);
                    }
                    return;
                } else {
                    setState(State.NotFound);
                }
            }

            if (onPickup) {
                log.info("Looking up pick up key", trimmed);
                const pickups = await findByPickupKey(platform, trimmed, deliveries ?? []);
                let pickupRecipient: Recipient|undefined = undefined; 
                if (pickups.length>0) {
                    const deliveries = getOccupancyDeliveries(pickups[0]);
                   deliveries?.forEach(d=>d.recipients.forEach(r=>{
                        if (r.pickupKey === trimmed) {
                            pickupRecipient = r;
                        }
                    }));
                }

                if (pickups.length > 0) {
                    onPickup({occupancies: pickups, pickupKey: trimmed, pickupRecipient});
                    return;
                } else {
                    setState(State.NotFound);
                }
            }

            if (onAdminLogin) {
                log.info("Looking key for staff", trimmed);
                const user = tenant?.users.find(user => user.accessCode == trimmed);
                if (user) {
                    onAdminLogin(user);
                    return
                }
            }
        } catch (error) {
            log.error("Could not retrieve occupancies", error);
        }

        setTimeout(() => {
            setState(State.Closed);
        }, 3000)
    }, [tenant, deliveries, platform, onStorage, onPickup, onAdminLogin, onCreateDelivery]);
    useKeyInput(handleKey);

    const handleBarcode = useCallback((barcode: Barcode) => handleKey(barcode.code), [handleKey]);
    useBarcodeListener(handleBarcode);

    const handleCard = useCallback((card?: NfcCard) => card && handleKey(card.uid), [handleKey]);
    useCardListener(handleCard);

    return (
        <Fragment>
            {children}
            <Dialog open={state != State.Closed} onBackdropClick={handleClose}>
                <DialogContent>
                    {state == State.Loading && t("LookingUpDelivery")}
                    {state == State.NotFound && t("DeliveryNotFound")}
                    {state == State.CannotStore && t("DeliveryCannotBeStored")}
                </DialogContent>
            </Dialog>
        </Fragment>
    )
}
